import {
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { apiFetch } from './api.ts';

interface Estado<T> {
  datos: T | null;
  cargando: boolean;
  error: string | null;
  recargar: () => void;
  setDatos: (d: T | null) => void;
}

/** Cada cuánto se refresca solo un useApi mientras la pestaña está visible. */
const REFRESCO_AUTO_MS = 15_000;

/**
 * Hook para GET con estado de carga/error.
 * Se refresca solo cada 15 s (y al volver a la pestaña) mientras esté visible,
 * en silencio: sin spinner y sin pisar datos ya mostrados por un fallo transitorio.
 */
export function useApi<T>(path: string | null): Estado<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState<boolean>(!!path);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const tieneDatos = useRef(false);
  const pathAnterior = useRef<string | null>(null);

  const recargar = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (path !== pathAnterior.current) {
      // Cambió la consulta: los datos viejos no sirven, volver al spinner.
      tieneDatos.current = false;
      pathAnterior.current = path;
    }
    if (!path) {
      setDatos(null);
      setCargando(false);
      return;
    }
    let cancelado = false;
    let enVuelo = false;

    const cargarDatos = (silencioso: boolean) => {
      if (silencioso && enVuelo) return;
      enVuelo = true;
      if (!silencioso) {
        setCargando(true);
        setError(null);
      }
      apiFetch<T>(path)
        .then((d) => {
          if (cancelado) return;
          tieneDatos.current = true;
          setDatos(d);
          setError(null);
        })
        .catch((e) => {
          if (!cancelado && !silencioso) setError(e instanceof Error ? e.message : 'Error');
        })
        .finally(() => {
          enVuelo = false;
          if (!cancelado && !silencioso) setCargando(false);
        });
    };

    // recargar() tras una acción con datos ya en pantalla → sin spinner.
    cargarDatos(tieneDatos.current);

    const refrescarSiVisible = () => {
      if (document.visibilityState === 'visible') cargarDatos(true);
    };
    const idIntervalo = window.setInterval(refrescarSiVisible, REFRESCO_AUTO_MS);
    window.addEventListener('focus', refrescarSiVisible);
    document.addEventListener('visibilitychange', refrescarSiVisible);

    return () => {
      cancelado = true;
      window.clearInterval(idIntervalo);
      window.removeEventListener('focus', refrescarSiVisible);
      document.removeEventListener('visibilitychange', refrescarSiVisible);
    };
  }, [path, tick]);

  return { datos, cargando, error, recargar, setDatos };
}

/** POST/PUT/DELETE helper con manejo de error. */
export async function apiPost<T = unknown>(
  path: string,
  body?: unknown,
  metodo: 'POST' | 'PUT' | 'DELETE' = 'POST',
): Promise<T> {
  return apiPostRaw<T>(path, body, metodo);
}

export async function apiPostRaw<T = unknown>(
  path: string,
  body?: unknown,
  metodo: 'POST' | 'PUT' | 'DELETE' = 'POST',
): Promise<T> {
  return apiFetch<T>(path, {
    method: metodo,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }): ReactNode {
  return (
    <div className="flex items-center justify-center py-10 text-neutro-500">
      <div className="mr-3 h-6 w-6 animate-spin rounded-full border-2 border-contorno-var border-t-primario-400" />
      <span className="text-sm">{texto}</span>
    </div>
  );
}

export function ErrorApi({ mensaje, onReintentar }: { mensaje: string; onReintentar?: () => void }): ReactNode {
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-estado-error">
      <p className="font-medium">{mensaje}</p>
      {onReintentar && (
        <button onClick={onReintentar} className="mt-2 text-sm underline">
          Reintentar
        </button>
      )}
    </div>
  );
}

export function Vacio({ texto }: { texto: string }): ReactNode {
  return (
    <div className="py-10 text-center text-neutro-400">
      <p>{texto}</p>
    </div>
  );
}

export function Badge({ color, children }: { color: 'verde' | 'rojo' | 'amarillo' | 'gris' | 'azul'; children: ReactNode }): ReactNode {
  const clases = {
    verde: 'badge-ok',
    rojo: 'badge-error',
    amarillo: 'badge-pendiente',
    gris: 'badge-neutral',
    azul: 'badge-info',
  };
  return <span className={`badge ${clases[color]}`}>{children}</span>;
}

export function Modal({
  abierto,
  titulo,
  onCerrar,
  children,
}: {
  abierto: boolean;
  titulo: string;
  onCerrar: () => void;
  children: ReactNode;
}): ReactNode {
  useEffect(() => {
    if (!abierto) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [abierto, onCerrar]);

  if (!abierto) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl border border-contorno-var">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-neutro-800">{titulo}</h2>
          <button
            onClick={onCerrar}
            className="rounded-lg p-1.5 text-neutro-400 hover:bg-superficie-100 hover:text-neutro-600"
            aria-label="Cerrar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function BotonAccion({
  variante = 'primario',
  children,
  ...props
}: {
  variante?: 'primario' | 'secundario' | 'peligro' | 'fantasma';
} & React.ButtonHTMLAttributes<HTMLButtonElement>): ReactNode {
  const clases = {
    primario: 'bg-primario-400 text-white hover:bg-primario-500',
    secundario: 'bg-superficie-200 text-neutro-800 hover:bg-superficie-300',
    peligro: 'bg-red-50 text-estado-error hover:bg-red-100',
    fantasma: 'text-neutro-600 hover:bg-superficie-100',
  };
  return (
    <button {...props} className={`boton ${clases[variante]} ${props.className ?? ''}`}>
      {children}
    </button>
  );
}

/** Formatea centésimos a $U con separador de miles. */
export function formatU(montoCent: number): string {
  const pesos = montoCent / 100;
  return '$U ' + pesos.toLocaleString('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Formatea fecha YYYY-MM-DD a DD/MM/AAAA. */
export function formatFecha(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
export function diaNombre(n: number): string {
  return DIAS[n - 1] ?? '?';
}

/** Pide confirmación antes de acción destructiva. */
export function confirmar(mensaje: string): boolean {
  return window.confirm(mensaje);
}
